import {
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalService } from '../../shared/services/modal.service';
import { hasStoredAccessToken } from '../../user/auth-tokens';
import { ReportsService, RequestedReport } from '../reports.service';
import { GuestOrderService, GuestOrderView } from '../guest-order.service';
import { JsonResponse } from '../../shared/api/backend-config';
import {
  reportOrderUrl,
  reportTitle,
} from '../order-report/choose-type/report-type/reports-info';
import { Subscription, filter, takeWhile, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { ReportStatusToTooltipPipe } from './report-status-to-tooltip.pipe';
import { StatusToTextPipe } from './status-to-text.pipe';
import { UtcToLocalPipe } from '../../shared/pipes/utc-to-local.pipe';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { MatDivider } from '@angular/material/divider';
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { IconComponent } from '../../shared/components/icon/icon.component';

export enum ReportStatus {
  // report was ordered but not paid with stripe
  // insufficient card is here as well
  ORDERED_NOT_PAID = 'ORDERED_NOT_PAID',
  // payment was made but there were tricky errors
  PAYMENT_ERROR = 'PAYMENT_ERROR',
  // report ordered, payment was successful
  ORDERED = 'ORDERED',
  // backend processes the report
  IN_PROGRESS_AUTOMATED = 'IN_PROGRESS_AUTOMATED',
  // backend got error processing the report
  AUTOMATION_ERROR = 'AUTOMATION_ERROR',
  // backend processed the report, manual review is needed
  IN_PROGRESS_MANUAL = 'IN_PROGRESS_MANUAL',
  // report is ready to be downloaded
  READY = 'READY',
}

@Component({
  selector: 'app-requested-reports',
  templateUrl: './requested-reports.component.html',
  styleUrls: ['./requested-reports.component.scss', '../../app.component.scss'],
  imports: [
    NgIf,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    NgClass,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgFor,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatDivider,
    IconComponent,
    RouterLink,
    RouterLinkActive,
    DatePipe,
    UtcToLocalPipe,
    StatusToTextPipe,
    ReportStatusToTooltipPipe,
  ],
})
export class RequestedReportsComponent implements OnInit {
  @ViewChild('table', { static: false }) table!: MatTable<RequestedReport>;

  public requestedReports: RequestedReport[] = [];
  public isSomeReportInProgress = false;

  private destroyRef = inject(DestroyRef);

  /** Whether this instance has listed the account's reports yet. */
  private loaded = false;

  /** A guest's just-paid order, read by the id this browser kept. */
  public guestOrder: GuestOrderView | null = null;

  private guestWatch?: Subscription;

  constructor(
    private reportsService: ReportsService,
    private modalService: ModalService,
    private router: Router,
    private guestOrders: GuestOrderService
  ) {}

  /**
   * GET /report/my needs an account, and a guest buyer has one only in the
   * backend: it was made from their Stripe email, they never signed in to it.
   */
  public get isGuest(): boolean {
    return !hasStoredAccessToken();
  }

  public get guestJustPaid(): boolean {
    return this.reportsService.guestJustPaid;
  }

  public get guestOrderReports(): RequestedReport[] {
    return this.guestOrder?.kind === 'order' ? this.guestOrder.reports : [];
  }

  /** Paid, as far as this browser knows - just back from Stripe, or holding a readable order. */
  public get guestHasPaid(): boolean {
    return this.guestJustPaid || this.guestOrder?.kind === 'order';
  }

  ngOnInit(): void {
    if (!this.isGuest) this.loadOrderedReports();
    this.watchGuestOrder();

    // The route is reused, so a visitor who was a guest when this instance was
    // made and has signed in since comes back to it, not to a fresh ngOnInit.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        filter((e) => e.urlAfterRedirects.startsWith('/reports/my-reports')),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.isGuest && !this.loaded) this.loadOrderedReports();
        this.watchGuestOrder();
      });

    this.reportsService.pollingSubj
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadOrderedReports();
      });
  }

  private loadOrderedReports(): void {
    this.loaded = true;
    this.requestedReports.length = 0;

    timer(0, 2000)
      .pipe(
        switchMap(() => this.reportsService.getRequestedReports()),
        tap((data: JsonResponse<RequestedReport[]>) => {
          this.requestedReports = data.object
            // Filter out NOT_PAID reports
            .filter((report) => report.status !== ReportStatus.ORDERED_NOT_PAID)
            .sort((a, b) => {
              // Check if either report has the status 'READY'
              // const isAReady = a.status === ReportStatus.READY;
              // const isBReady = b.status === ReportStatus.READY;

              // Prioritize 'READY' status
              // if (isAReady && !isBReady) return -1;
              // if (!isAReady && isBReady) return 1;

              // If statuses are the same compare by orderedAt
              const dateA = new Date(a.orderedAt);
              const dateB = new Date(b.orderedAt);
              return dateB.getTime() - dateA.getTime();
            });
          // Mark for polling
          this.isSomeReportInProgress = this.requestedReports.some((report) =>
            this.reportsService.isReportInProgress(report)
          );
        }),
        // Continue polling if there's at least one report in the ORDERED or IN_PROGRESS status
        // The second parameter (true) ensures the final emission (that doesn't satisfy the condition) is still passed through
        takeWhile(
          (data: JsonResponse<RequestedReport[]>) =>
            data.object.some((report) =>
              this.reportsService.isReportInProgress(report)
            ),
          true
        ),
        // Otherwise the 2s poll keeps hitting /report/my after the user has
        // navigated away, for as long as any report is still building.
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        complete: () => {
          // Once polling stops, mark that polling is done.
          this.isSomeReportInProgress = false;
        },
      });
  }

  /** The account already exists for a guest buyer, so this is a sign-in, not a sign-up. */
  public async signIn(): Promise<void> {
    const signedIn = await this.modalService.requestAuth({
      initialMode: 'signin',
      headerText: 'Sign in to see your reports',
      subHeaderText:
        'Use the email you paid with. No password yet? Pick "Forgot password" to set one.',
    });
    if (!signedIn) return;
    this.reportsService.guestJustPaid = false;
    // the account lists it from now on
    this.guestWatch?.unsubscribe();
    this.guestOrder = null;
    this.guestOrders.forget();
    this.loadOrderedReports();
  }

  private watchGuestOrder(): void {
    if (!this.isGuest || (this.guestWatch && !this.guestWatch.closed)) return;
    const requestId = this.guestOrders.storedRequestId();
    if (!requestId) return;
    this.guestWatch = this.guestOrders
      .watch(requestId, this.guestJustPaid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((view) => (this.guestOrder = view));
  }

  /**
   * Where "Make another" leads: this report's order form. Null for a report
   * the catalogue no longer has.
   */
  public makeAnotherUrl(type: string): string | null {
    return reportOrderUrl(type) ?? null;
  }

  /** In-app navigation for a plain click; a modified one opens the href. */
  public makeAnother(type: string, event: MouseEvent): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey)
      return;
    const url = this.makeAnotherUrl(type);
    if (!url) return;
    event.preventDefault();
    void this.router.navigateByUrl(url);
  }

  public statusTone(status: string): 'ready' | 'payment-error' | 'in-progress' {
    switch (status) {
      case ReportStatus.READY:
        return 'ready';
      case ReportStatus.PAYMENT_ERROR:
      case ReportStatus.AUTOMATION_ERROR:
        return 'payment-error';
      default:
        return 'in-progress';
    }
  }

  public handleLinkClick(report: RequestedReport): void {
    if (report.url) {
      window.open(report.url, '_blank');
    }
  }

  protected readonly columns = ['name', 'type', 'date', 'status', 'another'];
  protected readonly reportTitle = reportTitle;
}
